import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { notificationService, Notification } from '@/services/notificationService';
import { useAuthStore } from '@/stores/authStore';

const NotificationBell: React.FC = () => {
    const { accessToken, user } = useAuthStore();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch unread count
    const { data: countData } = useQuery({
        queryKey: ['notification-count'],
        queryFn: () => notificationService.getUnreadCount(),
        refetchInterval: 60000, // Refresh every minute
        staleTime: 30000,
    });

    // Fetch notifications when dropdown opens
    const { data: notificationsData, isLoading } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => notificationService.getNotifications(1, 10),
        enabled: isOpen,
    });

    // Mark as read mutation
    const markAsReadMutation = useMutation({
        mutationFn: (id: string) => notificationService.markAsRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notification-count'] });
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    // Mark all as read mutation
    const markAllAsReadMutation = useMutation({
        mutationFn: () => notificationService.markAllAsRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notification-count'] });
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    // WebSocket for real-time notifications
    useEffect(() => {
        const wsUrl = import.meta.env.VITE_WS_URL || 'http://localhost:3001';
        
        if (!accessToken) {
            console.warn('No auth token available for notification WebSocket connection');
            return;
        }
        
        // Socket.IO namespace is specified in the URL - Socket.IO handles the /socket.io/ path internally
        const socket: Socket = io(`${wsUrl}/beyaz-masa`, {
            path: '/socket.io/',
            auth: { token: accessToken, userId: user?.id },
            transports: ['websocket', 'polling'],
        });

        socket.on('notification:received', () => {
            // Refetch notification count when new notification arrives
            queryClient.invalidateQueries({ queryKey: ['notification-count'] });
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        });

        return () => {
            socket.disconnect();
        };
    }, [accessToken, user?.id, queryClient]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNotificationClick = (notification: Notification) => {
        // Mark as read
        if (!notification.read) {
            markAsReadMutation.mutate(notification.id);
        }

        // Navigate to ticket if applicable
        if (notification.data?.ticketId) {
            navigate(`/beyaz-masa/tickets/${notification.data.ticketId}`);
            setIsOpen(false);
        }
    };

    const unreadCount = countData?.count || 0;

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Bildirimler"
            >
                <Bell className="w-6 h-6 text-gray-600" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                        <h3 className="font-semibold text-gray-900">Bildirimler</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={() => markAllAsReadMutation.mutate()}
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                disabled={markAllAsReadMutation.isPending}
                            >
                                <CheckCheck className="w-4 h-4" />
                                Tümünü oku
                            </button>
                        )}
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                        {isLoading ? (
                            <div className="p-4 text-center text-gray-500">Yükleniyor...</div>
                        ) : !notificationsData?.notifications?.length ? (
                            <div className="p-8 text-center text-gray-500">
                                <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                <p>Henüz bildirim yok</p>
                            </div>
                        ) : (
                            notificationsData.notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    className={`px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50 ${!notification.read ? 'bg-blue-50' : ''
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="flex-1">
                                            <p className={`text-sm ${!notification.read ? 'font-semibold' : 'font-normal'} text-gray-900`}>
                                                {notification.title}
                                            </p>
                                            <p className="text-sm text-gray-600 mt-0.5">{notification.message}</p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {formatDistanceToNow(new Date(notification.createdAt), {
                                                    addSuffix: true,
                                                    locale: tr,
                                                })}
                                            </p>
                                        </div>
                                        {!notification.read && (
                                            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
                                        )}
                                        {notification.data?.ticketId && (
                                            <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    {notificationsData?.pagination && notificationsData.pagination.total > 10 && (
                        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => {
                                    navigate('/notifications');
                                    setIsOpen(false);
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 w-full text-center"
                            >
                                Tüm bildirimleri gör
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
